import React, { useEffect, useRef, useState } from 'react'
import { returnFareName, REVAMP_FARE_CHIP_CLASS } from '@/constants/components/fareType'
import { Badge, Container, Radio, TooltipV2, Typography } from '@cleartrip/ct-design-components'
import { RAVEN_ATTRIBUTE_NAME, RAVEN_EVENT_NAME, RAVEN_PAGE_NAME } from '@/analytics/raven/ravenConstants'
import { Raven } from '@/analytics/raven'
import Clevertap from '@/analytics/clevertap/index'
import { ACTION_NAMES } from '@/constants/analytics'
import BorderAnimation from '@/components/home/CFW/HomePageCallout/BorderAnimation'
import { EMPTY_BLOCK_FUNCTION } from '@/constants/pages/common'

interface FareType {
    code: string
    fare: string
    active: boolean
    toolTipMsg?: string
    toolTipErrorMsg?: string
    subtitle?: string
}

interface SplFareTypeProps {
    activeFare: string
    fareTypeList: FareType[]
    tripType: string
    handleClick: (type: string) => void
    variant?: 'home' | 'srp'
    isIntl?: boolean
    isThemed?: boolean
    isRevamp?: boolean
}

const SplFareType: React.FC<SplFareTypeProps> = ({ activeFare, fareTypeList, tripType, handleClick, variant = 'home', isIntl = false, isThemed = false, isRevamp = false }) => {
    function ClickHandler(type: string, active: boolean) {
        if (!active) {
            return
        }
        Raven.push({
            eventName: RAVEN_EVENT_NAME.HOME_PTC_FARE_SELECTED,
            eventData: {
                action_name: RAVEN_EVENT_NAME.HOME_PTC_FARE_SELECTED,
                page_name: RAVEN_PAGE_NAME.FLIGHTS_HOME,
                a_fare_type: type,
                action_type: 'click'
            }
        })
        handleClick(type)
    }

    const disableClickHandler = (ele: FareType) => {
        Clevertap.event('Air_UI_Action', {
            action_name: ACTION_NAMES.ptcFareSelectionError,
            ptc_fare: returnFareName(ele.code),
            is_ptc_selectable: returnFareName(activeFare) === 'Regular fare' ? 'no' : 'yes',
            ptc_fare_selection_error: `You cannot select ${ele.fare} with infants added to the journey`,
            journey_type_uiaction: tripType === 'O' ? 'one-way' : 'round-trip'
        })
    }

    const onHoverHandler = (code: string) => {
        if (code !== '') {
            Raven.push({
                eventName: RAVEN_EVENT_NAME.HOME_PTC_ICON_HOVER,
                eventData: {
                    action_name: RAVEN_ATTRIBUTE_NAME.HP_PTC_ICON_HOVER,
                    page_name: RAVEN_PAGE_NAME.FLIGHTS_HOME
                }
            })
        }
        Clevertap.event('Air_UI_Action', {
            action_name: ACTION_NAMES.ptcIconHover,
            action_type: 'hover',
            ptc_fare: returnFareName(code),
            journey_type_uiaction: tripType === 'O' ? 'one-way' : 'round-trip'
        })
    }


    const fareTypes = fareTypeList.map((ele, idx) => {
        const isSelected = ele.code === activeFare

        return (
            <div onClick={() => ClickHandler(ele.code, ele.active)} key={idx}>
                <TooltipV2
                    position="bottom"
                    styleConfig={{
                        tooltipContainer: {
                            css: {
                                color: 'white',
                                width: '300px'
                            },
                            className: 'p-3'
                        }
                    }}
                    labelContainer={
                        isRevamp ? (
                            <div
                                className={REVAMP_FARE_CHIP_CLASS}
                                style={{ opacity: ele.active ? 1 : 0.5 }}
                                onMouseEnter={() => onHoverHandler(ele.code)}
                            >
                                <Radio
                                    checked={isSelected}
                                    id={`fare-${ele.code}`}
                                    name="srpFareType"
                                    value={ele.code}
                                    onChange={EMPTY_BLOCK_FUNCTION}
                                />
                                <Typography variant="B3">{ele.fare}</Typography>
                            </div>
                        ) : (
                        <div onMouseEnter={() => onHoverHandler(ele.code)}>
                            <BorderAnimation className='br-6' startAnimation={isIntl && ele.code === 'student' && !isSelected && !isThemed}>
                                <Container
                                    className="flex flex-middle c-pointer br-6 p-2"
                                    style={{
                                        width: '140px',
                                        height: '46px',
                                        border: isSelected ? '1px solid #1A1A1A' : (isIntl && ele.code === 'student' && !isThemed) ? 'none': '1px solid #E0E0E0',
                                        backgroundColor: isSelected ? '#F5F5F5' : isThemed ? 'rgba(255, 255, 255, 0.75)' : 'transparent',
                                        ...(isThemed && !isSelected
                                            ? { backdropFilter: 'blur(4.45px)', WebkitBackdropFilter: 'blur(4.45px)' }
                                            : {}),
                                        opacity: ele.active ? 1 : 0.5,
                                        gap: variant === 'srp' ? '12px' : '8px'
                                    }}
                                >
                                    <Container className="flex flex-column">
                                        <Container className="flex flex-middle" style={{ gap: '4px' }}>
                                            <Typography variant="B3">
                                                {ele.fare}
                                            </Typography>
                                            {isIntl && ele.code === 'student' && (
                                                <Badge
                                                    text="NEW"
                                                    colorScheme="orange500"
                                                    size="nano"
                                                    styleConfig={{
                                                        textNodeContainer: {
                                                            css: {
                                                                color: '#FFFFFF',
                                                            }
                                                        }
                                                    }}
                                                />
                                            )}
                                        </Container>
                                        {ele.subtitle && (
                                            <Typography variant="B4" color="success">
                                                {ele.subtitle}
                                            </Typography>
                                        )}
                                    </Container>
                                </Container>
                            </BorderAnimation>
                        </div>
                        )
                    }
                >
                    {ele.active ? ele.toolTipMsg : ele.toolTipErrorMsg}
                </TooltipV2>
            </div>
        )
    })

    const fareListClassName = variant === 'srp' && !isRevamp ? 'flex flex-middle flex-cg-3' : 'flex flex-middle flex-cg-2'

    return (
        <Container className={fareListClassName}>
            {fareTypes}
        </Container>
    )
}

export default SplFareType
